export interface Experience {
  id: string;
  title: string;
  company: string;
  location: string;
  duration: string;
  type: 'current' | 'research' | 'teaching' | 'leadership' | 'development';
  description: string;
  highlights: string[];
  backgroundImage?: string;
  courses?: Course[];
  links?: { text: string; url: string }[];
  skills?: string[];
}

export interface Course {
  code: string;
  name: string;
  responsibilities: string[];
}

export interface Achievement {
  id: string;
  title: string;
  organization: string;
  date: string;
  place: string;
  prize: string;
  description: string;
  links: Link[];
  backgroundImage?: string;
  linkedinPost?: LinkedinPost;
}

export interface LinkedinPost {
  id: string;
  text: string;
  content: string;
  url: string;
  image?: string;
  imagePosition?: string;
}

export interface Link {
  text: string;
  url: string;
  type: 'external' | 'video' | 'linkedin';
}

export interface GalleryItem {
  id: string;
  image: string;
  caption: string;
  category: 'action-africa' | 'enactus' | 'teaching' | 'research';
}

export interface Certification {
  id: string;
  title: string;
  organization: string;
  issueDate: string;
  expirationDate?: string;
  credentialId?: string;
  credentialUrl?: string;
  skills: string[];
  description?: string;
  logo?: string;
}

export interface Education {
  id: string;
  degree: string;
  institution: string;
  location: string;
  duration: string;
  type: 'master' | 'bachelor' | 'engineering' | 'diploma';
  status: 'current' | 'completed' | 'expected';
  description?: string;
  gpa?: string;
  honors?: string[];
  coursework?: string[];
}

export interface PersonalInfo {
  name: string;
  tagline: string;
  profileImage: string;
  stats: Stat[];
  about: string;
  contact: ContactInfo;
}

export interface Stat {
  number: string;
  label: string;
}

export interface ContactInfo {
  email: string;
  linkedin?: string;
  github?: string;
  gitlab?: string;
}

// Portfolio Data
export const personalInfo: PersonalInfo = {
  name: "Achref Samoud",
  tagline: "Software Engineer • DevOps Research • Cloud Infrastructure • Montreal, QC",
  profileImage: "/images/profile.jpg",
  stats: [],
  about: "I am a software engineer based in Montreal, Quebec, with a focus on DevOps research, cloud infrastructure, and developer tooling. I specialize in CI/CD automation, containerization, and production-ready applications using React, TypeScript, and modern web technologies. Currently pursuing a Master in Software Engineering at ÉTS Montreal, conducting research at Telus, and working as a Teaching Assistant for DevOps and Software Architecture courses.",
  contact: {
    email: "achref.samoud.1@ens.etsmtl.ca",
    linkedin: "https://www.linkedin.com/in/achref-samoud/",
    github: "https://github.com/AchrefSamoud",
    gitlab: "https://gitlab.com/achrefsam"
  }
};

export const experiences: Experience[] = [
  {
    id: "telus-research",
    title: "Research Assistant and Intern",
    company: "Telus",
    location: "Montreal, Canada",
    duration: "January 2025 — Present",
    type: "research",
    description: "Applied research on DevOps process improvement, focusing on CI/CD pipeline analysis, digital twin modeling, and systematic pipeline evolution.",
    highlights: [
      "Designed a model-driven Digital Twin for CI/CD pipelines using BPMN as a modeling backbone to transform raw CI configurations into structured process representations",
      "Developed a framework enabling visual abstraction of pipeline structure, failure tracing, and systematic version-to-version comparison",
      "Validated the approach on open-source CI/CD projects; ongoing work targets integration of prescriptive improvement services",
      "Published research paper: \"A Model-Driven Digital Twin for the Systematic Improvement of DevOps Pipelines\" (arXiv:2604.02077)"
    ],
    backgroundImage: "/images/gallery-research-1.jpg",
    links: [
      { text: "View Paper (arXiv)", url: "https://arxiv.org/abs/2604.02077" }
    ],
    skills: ['Python', 'GitLab CI/CD', 'BPMN', 'Digital Twin', 'DevOps']
  },
  {
    id: "ets-teaching-log680",
    title: "Teaching Assistant LOG680: Introduction to the DevOps Approach",
    company: "École de technologie supérieure (ÉTS)",
    location: "Montreal, Quebec, Canada",
    duration: "May 2025 — August 2025",
    type: "teaching",
    description: "Lab instructor for a project-based course focused on practical DevOps foundations.",
    highlights: [
      "Setting up GitLab environments with structured issue tracking, branching policies, and Kanban boards",
      "Designing and maintaining full CI/CD pipelines including pre-commit hooks, automated testing, containerization (Docker), and deployment to Kubernetes",
      "Developing and exposing application metrics through custom APIs and visualizing them using Grafana",
      "Configuring and maintaining technical infrastructure used by students throughout the course, including access management (Kubernetes namespaces, Grafana instances)"
    ],
    backgroundImage: "/images/gallery-teaching-1.jpg",
    links: [
      { text: "ÉTS Montreal", url: "https://www.etsmtl.ca/" }
    ],
    skills: ['Docker', 'Kubernetes', 'GitLab CI/CD', 'Grafana', 'Prometheus']
  },
  {
    id: "ets-teaching-log430",
    title: "Teaching Assistant LOG430: Software Architecture",
    company: "École de technologie supérieure (ÉTS)",
    location: "Montreal, Quebec, Canada",
    duration: "May 2025 — Present",
    type: "teaching",
    description: "Lab instructor focused on software architecture design, documentation, and analysis.",
    highlights: [
      "Developed hands-on lab assignments tailored to the course content and learning objectives",
      "Developed hands-on lab scenarios covering software architecture design, documentation, and analysis, based on quality attributes such as modularity, performance, and maintainability",
      "Collaborated with faculty to ensure pedagogical alignment of the labs with course objectives",
      "Provided technical support and guidance to students during lab sessions"
    ],
    backgroundImage: "/images/gallery-teaching-1.jpg",
    links: [
      { text: "ÉTS Montreal", url: "https://www.etsmtl.ca/" }
    ],
    skills: ['Software Architecture', 'UML', 'Java', 'Arc42']
  },
  {
    id: "vendogram",
    title: "Instructor & Tech Lead",
    company: "Vendogram AI Academy",
    location: "Brossard, Canada",
    duration: "June 2025 — October 2025",
    type: "development",
    description: "Taught programming and technology fundamentals to youth, designed hands-on curricula, and led technical operations for an AI-focused education academy.",
    highlights: [
      "Designed and delivered programming curricula for youth learners covering Python, robotics, and AI fundamentals",
      "Created hands-on learning materials and project-based assignments tailored to different age groups",
      "Managed educational technology infrastructure and automated internal workflow processes",
      "Mentored students through technical projects, fostering problem-solving and computational thinking skills"
    ],
    backgroundImage: "/images/vendogram.jpg",
    links: [
      { text: "Vendogram AI Academy", url: "https://www.vendogram.com/" }
    ],
    skills: ['Python', 'React', 'Curriculum Design', 'AI']
  },
  {
    id: "mitacs-research",
    title: "Mitacs Globalink Research Intern",
    company: "École de technologie supérieure (ÉTS)",
    location: "Montreal, Canada",
    duration: "May 2024 — August 2024",
    type: "development",
    description: "Advanced architectural traceability research with practical software development applications.",
    highlights: [
      "Conducted literature review on architectural traceability methodologies",
      "Designed mechanism to trace architectural tactic constraints to source code",
      "Developed VS Code extension for architectural constraint violation detection",
      "Implemented CI/CD pipeline for automated extension deployment to marketplace"
    ],
    backgroundImage: "/images/gallery-research-1.jpg",
    skills: ['VS Code Extension', 'TypeScript', 'CI/CD', 'Python']
  },
  {
    id: "enactus-director",
    title: "Director of Projects",
    company: "Enactus ISI",
    location: "Ariana, Tunisia",
    duration: "2021 — 2023",
    type: "leadership",
    description: "Led international competition teams to multiple victories and $45K+ in prize money.",
    highlights: [
      "Directed recruitment and supervised operations of social impact projects",
      "Represented Tunisia in international competitions with record-breaking results",
      "Managed R&D teams and coordinated strategic partnerships"
    ],
    backgroundImage: "/images/gallery-action-1.jpg",
    links: [
      { text: "Enactus ISI", url: "https://enactus.org.tn/" }
    ],
    skills: ['Leadership', 'Project Management', 'Strategy']
  },
  {
    id: "comunik-mobile",
    title: "Mobile Developer Intern",
    company: "ComunikCRM",
    location: "Ariana, Tunisia",
    duration: "July 2023 — September 2023",
    type: "development",
    description: "Designed and shipped a React Native HelpDesk mobile application to complement ComunikCRM's existing web platform, improving customer support accessibility.",
    highlights: [
      "Built a full HelpDesk mobile app from scratch with ticket management, real-time tracking, and secure user authentication",
      "Delivered features end-to-end: requirements, UI design, implementation, and testing",
      "Applied Scrum methodology with iterative sprints, managing tasks and progress through ClickUp",
      "Integrated REST APIs using Postman and maintained codebase versioning with Git/GitHub"
    ],
    backgroundImage: "/images/gallery-research-1.jpg",
    links: [
      { text: "ComunikCRM", url: "https://www.comunikcrm.com/" }
    ],
    skills: ['React Native', 'TypeScript', 'REST APIs', 'Scrum']
  },
  {
    id: "cognition-mern",
    title: "Web Developer — MERN Stack",
    company: "Cognition LLC",
    location: "Ariana, Tunisia",
    duration: "February 2021 — July 2021",
    type: "development",
    description: "Built a web-based medical platform integrating Computer-Aided Diagnostics (CADs) for radiology and dermatology services.",
    highlights: [
      "Developed e-health.tn, a full-stack medical platform supporting radiology and dermatology CAD workflows",
      "Implemented user authentication, patient management, and diagnostic result tracking with ReactJS and Node.js",
      "Designed a scalable three-tier architecture backed by MongoDB for secure patient data management",
      "Containerized the application with Docker and managed source control with Git/Bitbucket"
    ],
    backgroundImage: "/images/gallery-research-1.jpg",
    skills: ['React', 'Node.js', 'MongoDB', 'Docker']
  },
  {
    id: "comunik-web",
    title: "Web Developer Intern",
    company: "ComunikCRM",
    location: "Ariana, Tunisia",
    duration: "July 2020 — September 2020",
    type: "development",
    description: "Developed and deployed a web-based search engine for deliberations of Tunisia's People's Representative Assembly.",
    highlights: [
      "Built a full-text search engine over parliamentary deliberations data using PHP and MySQL",
      "Designed and implemented the database schema for efficient querying of legislative records",
      "Deployed the application to production"
    ],
    backgroundImage: "/images/gallery-research-1.jpg",
    links: [
      { text: "ComunikCRM", url: "https://www.comunikcrm.com/" }
    ],
    skills: ['PHP', 'MySQL', 'JavaScript', 'CSS']
  }
];

export const achievements: Achievement[] = [
  {
    id: "action-africa",
    title: "Action With Africa Challenge",
    organization: "Enactus Germany",
    date: "October 2023",
    place: "2nd Place",
    prize: "$20,000",
    description: "Secured 2nd place among 190 international teams, earning $20,000 prize money. Led the Q&A session during our final presentation in Utrecht, Netherlands.",
    backgroundImage: "/images/action-with-africa.jpg",
    links: [
      { text: "View Results", url: "https://action-with-africa.com/awa-results-2023/", type: "external" },
      { text: "Watch Presentation", url: "https://www.youtube.com/watch?v=jz8Au1E7yC8&t=3320s", type: "video" },
      { text: "LinkedIn Post", url: "https://www.linkedin.com/feed/update/urn:li:activity:7126561959247781888/", type: "linkedin" }
    ],
    linkedinPost: {
      id: "7126561959247781888",
      text: "🏆 Thrilled to share that our team secured 2nd place at the Action With Africa Challenge! Among 190 participating teams, we made it to the top 6 and claimed the runner-up position in Utrecht, Netherlands.",
      content: "🏆 Thrilled to share that our team secured 2nd place at the Action With Africa Challenge! Among 190 participating teams, we made it to the top 6 and claimed the runner-up position in Utrecht, Netherlands.",
      url: "https://www.linkedin.com/feed/update/urn:li:activity:7126561959247781888/",
      image: "/images/linkedin-action-africa-post.jpg",
      imagePosition: "center top"
    }
  },
  {
    id: "enactus-worldcup",
    title: "Enactus World Cup",
    organization: "Enactus",
    date: "November 2022",
    place: "2nd Place",
    prize: "$25,000",
    description: "National Competition Champion 2022 and secured 2nd place in the Enactus World Cup, winning $25,000 prize money.",
    backgroundImage: "/images/enactus-world-cup.jpg",
    links: [
      { text: "LinkedIn Post", url: "https://www.linkedin.com/feed/update/urn:li:activity:6949739918944313345/", type: "linkedin" }
    ],
    linkedinPost: {
      id: "6949739918944313345",
      text: "🎉 Incredible milestone! Our Enactus team achieved National Champion status and secured 2nd place at the Enactus World Cup. Proud of what we accomplished together!",
      content: "🎉 Incredible milestone! Our Enactus team achieved National Champion status and secured 2nd place at the Enactus World Cup. Proud of what we accomplished together!",
      url: "https://www.linkedin.com/feed/update/urn:li:activity:6949739918944313345/",
      image: "/images/Enactus World Cup Celebration.jpg",
      imagePosition: "center bottom"
    }
  }
];

export const galleryItems: GalleryItem[] = [
  {
    id: "gallery-1",
    image: "/images/gallery-action-1.jpg",
    caption: "Utrecht Finals Presentation",
    category: "action-africa"
  },
  {
    id: "gallery-2",
    image: "/images/gallery-action-2.jpg",
    caption: "2nd Place Award Ceremony",
    category: "action-africa"
  },
  {
    id: "gallery-3",
    image: "/images/Enactus World Cup Celebration.jpg",
    caption: "Enactus World Cup Celebration",
    category: "enactus"
  },
  {
    id: "gallery-4",
    image: "/images/Enactus World Cup Cultural exchange.jpg",
    caption: "Enactus World Cup Cultural Exchange",
    category: "enactus"
  },
  {
    id: "gallery-5",
    image: "/images/gallery-enactus-1.jpg",
    caption: "Enactus World Cup Team",
    category: "enactus"
  },
  {
    id: "gallery-6",
    image: "/images/gallery-enactus-2.jpg",
    caption: "Victory Celebration",
    category: "enactus"
  },
  {
    id: "gallery-7",
    image: "/images/gallery-teaching-1.jpg",
    caption: "AI Infrastructure Development at Vendogram",
    category: "research"
  },
  {
    id: "gallery-8",
    image: "/images/gallery-research-1.jpg",
    caption: "Research at ÉTS",
    category: "research"
  }
];

export const certifications: Certification[] = [
  // Microsoft Certifications (En avant)
  {
    id: "azure-fundamentals",
    title: "Microsoft Certified: Azure Fundamentals",
    organization: "Microsoft",
    issueDate: "April 2023",
    credentialId: "f709-4472",
    credentialUrl: "https://www.credly.com/badges/azure-fundamentals",
    skills: ["Microsoft Azure", "Cloud Computing", "Azure Services"],
    description: "Foundational knowledge of cloud services and Microsoft Azure platform capabilities.",
    logo: "/images/certifications/azure fundamentals.png"
  },
  {
    id: "azure-data-fundamentals",
    title: "Microsoft Certified: Azure Data Fundamentals",
    organization: "Microsoft",
    issueDate: "April 2023",
    credentialId: "f727-5964",
    credentialUrl: "https://www.credly.com/badges/azure-data-fundamentals",
    skills: ["Azure", "Data Analytics", "SQL", "NoSQL", "Data Warehousing"],
    description: "Core data concepts and Microsoft Azure data services including relational and non-relational data.",
    logo: "/images/certifications/azure-data-fundamentals-600x600.png"
  },
  
  // AWS Certifications
  {
    id: "aws-generative-ai",
    title: "AWS Educate Introduction to Generative AI",
    organization: "Amazon Web Services (AWS)",
    issueDate: "March 2025",
    credentialUrl: "/images/certifications/AWS generative AI.png",
    skills: ["Generative AI", "AWS", "Machine Learning", "AI Development"],
    description: "Introduction to generative AI concepts and AWS AI/ML services.",
    logo: "/images/certifications/AWS generative AI.png"
  },
  {
    id: "aws-cloud-essentials",
    title: "AWS Cloud Technical Essentials",
    organization: "Amazon Web Services (AWS)",
    issueDate: "July 2023",
    credentialId: "4J9GSQ35YMZ6",
    credentialUrl: "/images/certifications/Aws Technical essentials.pdf",
    skills: ["AWS", "Cloud Computing", "EC2", "S3", "VPC", "Lambda", "DynamoDB", "IAM"],
    description: "Technical foundations of AWS cloud services including compute, storage, networking, and security.",
    logo: "/images/certifications/AWS generative AI.png"
  },
  {
    id: "aws-security-fundamentals",
    title: "AWS Fundamentals: Addressing Security Risk",
    organization: "Amazon Web Services (AWS)",
    issueDate: "July 2023",
    credentialId: "74ZFNK7GM2ZR",
    credentialUrl: "/images/certifications/AWS Fundamentals Addressing Security Risk.pdf",
    skills: ["AWS Security", "Identity and Access Management", "CloudWatch", "VPC Security"],
    description: "Security best practices and risk management in AWS cloud environments.",
    logo: "/images/certifications/AWS generative AI.png"
  },

  // KodeKloud Cloud & DevOps Certifications
  {
    id: "ansible-basics",
    title: "Learn Ansible Basics - Beginners Course",
    organization: "KodeKloud",
    issueDate: "October 2025",
    credentialId: "b3713bc9-a16d-401a-8808-dbe5d5d1105b",
    credentialUrl: "/images/certifications/learn ansible kodekloud.pdf",
    skills: ["Ansible", "DevOps", "Automation", "Infrastructure as Code"],
    description: "Fundamentals of Ansible automation, playbooks, and configuration management.",
    logo: "/images/certifications/kodekloudpng.avif"
  },
  {
    id: "kubernetes-beginners",
    title: "Kubernetes for the Absolute Beginners - Hands-on Tutorial",
    organization: "KodeKloud",
    issueDate: "January 2025",
    credentialId: "6a0d66ed-e98c-4dfd-bfa2-39d6f37cc22a",
    credentialUrl: "/images/certifications/Kubernetes for the Absolute Beginners - Hands-on Tutorial.pdf",
    skills: ["Kubernetes", "Container Orchestration", "DevOps", "Cloud Native"],
    description: "Hands-on experience with Kubernetes fundamentals and container orchestration.",
    logo: "/images/certifications/kodekloudpng.avif"
  },
  {
    id: "docker-training",
    title: "Docker Training Course for the Absolute Beginner",
    organization: "KodeKloud",
    issueDate: "December 2024",
    credentialId: "e7ff5b2d-9560-4cbe-a1a4-407051e181cd",
    credentialUrl: "https://kodekloud.com/certificate-verification",
    skills: ["Docker", "Containerization", "DevOps", "Application Deployment"],
    description: "Complete Docker fundamentals including containers, images, and deployment strategies.",
    logo: "/images/certifications/kodekloudpng.avif"
  },
  
  // IBM DevOps Certification (Bonus)
  {
    id: "ibm-devops-cloud",
    title: "IBM DevOps, Cloud, and Agile Foundations Specialization",
    organization: "IBM",
    issueDate: "2024",
    credentialUrl: "/images/certifications/IBM DevOps, Cloud, and.pdf",
    skills: ["DevOps", "Cloud Computing", "Agile", "IBM Cloud", "CI/CD"],
    description: "Comprehensive foundations of DevOps practices, cloud computing principles, and agile methodologies with IBM technologies.",
    logo: "/images/certifications/ibm-logo-black-transparent.png"
  }
];

export const education: Education[] = [
  {
    id: "ets-master",
    degree: "Master (M. Sc. A.) in Software Engineering",
    institution: "École de technologie supérieure (ÉTS)",
    location: "Montreal, QC, Canada",
    duration: "01/2025–01/2027",
    type: "master",
    status: "current",
    description: "Currently maintaining a 4.06/4.3 GPA."
  },
  {
    id: "isi-master",
    degree: "Master in Software Engineering",
    institution: "Higher Institute of Computer Science (ISI)",
    location: "Tunisia",
    duration: "09/2023–11/2024",
    type: "master",
    status: "completed",
    description: "Graduated with honors."
  },
  {
    id: "isi-engineering",
    degree: "Software Engineering degree",
    institution: "Higher Institute of Computer Science (ISI)",
    location: "Tunisia", 
    duration: "09/2021–09/2024",
    type: "engineering",
    status: "completed",
    description: "Graduated with honors."
  },
  {
    id: "isi-bachelor",
    degree: "Bachelor's in Computer Systems and Software",
    institution: "Higher Institute of Computer Science (ISI)",
    location: "Tunisia",
    duration: "09/2018–07/2021", 
    type: "bachelor",
    status: "completed",
    description: "Graduated with honors."
  }
];

// Main export object
export const portfolioData = {
  personalInfo,
  experiences,
  achievements,
  certifications,
  education,
  gallery: galleryItems,
  contact: {
    email: personalInfo.contact.email,
    phone: "+1 (555) 123-4567",
    location: "Montreal, QC, Canada"
  }
};